import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useVideoCall } from '../hooks/useVideoCall';
import VideoParticipant from './VideoParticipant';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TwilioVideoCall = ({ matchId, matchName, matchPhoto, callType = 'video', onClose }) => {
  const navigate = useNavigate();
  const {
    room,
    participants,
    error,
    isConnecting,
    localAudioEnabled,
    localVideoEnabled,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo
  } = useVideoCall();

  const [callId, setCallId] = useState(null);
  const [callStatus, setCallStatus] = useState('initiating'); // initiating, connecting, connected, ended

  useEffect(() => {
    // Initiate call and get token
    const initiateCall = async () => {
      try {
        setCallStatus('connecting');

        // Get video token
        const tokenResponse = await axios.post(
          `${API}/video/token`,
          { match_id: matchId, call_type: callType },
          { withCredentials: true }
        );

        const { token, room_name } = tokenResponse.data;

        // Join the room
        await joinRoom(token, room_name, {
          audio: true,
          video: callType === 'video'
        });

        setCallStatus('connected');
      } catch (err) {
        console.error('Failed to initiate call:', err);
        toast.error('Failed to start call');
        if (onClose) onClose();
      }
    };

    initiateCall();

    return () => {
      leaveRoom();
    };
  }, [matchId, callType, joinRoom, leaveRoom, onClose]);

  const handleEndCall = async () => {
    try {
      leaveRoom();
      setCallStatus('ended');
      toast.success('Call ended');
      if (onClose) onClose();
    } catch (err) {
      console.error('Error ending call:', err);
      if (onClose) onClose();
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-xl font-bold text-red-600 mb-4">Call Error</h3>
          <p className="text-gray-700 mb-6">{error}</p>
          <Button onClick={() => onClose && onClose()} className="w-full">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {matchPhoto && (
            <img src={matchPhoto} alt={matchName} className="w-10 h-10 rounded-full" />
          )}
          <div>
            <h2 className="text-white font-semibold">{matchName}</h2>
            <p className="text-gray-400 text-sm">
              {isConnecting ? 'Connecting...' : callStatus === 'connected' ? 'Connected' : 'Calling...'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleEndCall}
          variant="ghost"
          size="icon"
          className="text-white hover:bg-gray-700"
        >
          <X className="w-6 h-6" />
        </Button>
      </div>

      {/* Video Grid */}
      <div className="flex-1 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-auto">
        {/* Local Participant */}
        {room && room.localParticipant && (
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            <VideoParticipant participant={room.localParticipant} isLocal={true} />
          </div>
        )}

        {/* Remote Participants */}
        {participants.map(participant => (
          <div key={participant.sid} className="relative bg-black rounded-lg overflow-hidden aspect-video">
            <VideoParticipant participant={participant} isLocal={false} />
          </div>
        ))}

        {/* Waiting for other participant */}
        {participants.length === 0 && !isConnecting && (
          <div className="flex items-center justify-center bg-gray-800 rounded-lg aspect-video">
            <div className="text-center text-white">
              <Phone className="w-16 h-16 mx-auto mb-4 animate-pulse" />
              <p>Waiting for {matchName} to join...</p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-gray-800 p-6">
        <div className="flex justify-center items-center space-x-4">
          {/* Mute/Unmute Audio */}
          <Button
            onClick={toggleAudio}
            size="lg"
            className={`rounded-full w-14 h-14 ${
              localAudioEnabled
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-red-600 hover:bg-red-700'
            }`}
            title={localAudioEnabled ? 'Mute' : 'Unmute'}
          >
            {localAudioEnabled ? (
              <Mic className="w-6 h-6 text-white" />
            ) : (
              <MicOff className="w-6 h-6 text-white" />
            )}
          </Button>

          {/* Toggle Video (only for video calls) */}
          {callType === 'video' && (
            <Button
              onClick={toggleVideo}
              size="lg"
              className={`rounded-full w-14 h-14 ${
                localVideoEnabled
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              title={localVideoEnabled ? 'Stop Video' : 'Start Video'}
            >
              {localVideoEnabled ? (
                <Video className="w-6 h-6 text-white" />
              ) : (
                <VideoOff className="w-6 h-6 text-white" />
              )}
            </Button>
          )}

          {/* End Call */}
          <Button
            onClick={handleEndCall}
            size="lg"
            className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700"
            title="End Call"
          >
            <PhoneOff className="w-7 h-7 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TwilioVideoCall;
